import { useEffect, useState } from "react";
import ArticleCard from "../../components/client-side/blogs/ArticleCard";
import { slugify } from "../../utils/slugify";
import { getAllBlogs } from "../../controllers/homeController"; // uses your function

function formatDate(v) {
  try {
    const d = new Date(v);
    return isNaN(d)
      ? ""
      : d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
  } catch {
    return "";
  }
}

const SkeletonCard = () => (
  <div className="rounded-2xl overflow-hidden text-black dark:text-textDark bg-background dark:bg-darkbg shadow-sm animate-pulse">
    <div className="w-full aspect-[16/11] bg-muted/20 dark:bg-white/10" />
    <div className="mt-3 h-3 w-24 bg-muted/30 dark:bg-white/20 rounded" />
    <div className="mt-2 h-5 w-4/5 bg-muted/30 dark:bg-white/20 rounded" />
    <div className="mt-2 h-4 w-3/5 bg-muted/20 dark:bg-white/10 rounded" />
  </div>
);

const BlogsPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [displayedBlogs, setDisplayedBlogs] = useState([]);
  const [itemsPerPage] = useState(8);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const list = await getAllBlogs();
      if (alive) {
        // Sort by newest first (publishedAt descending)
        const sorted = [...list].sort((a, b) => {
          const dateA = new Date(a.publishedAt || 0).getTime();
          const dateB = new Date(b.publishedAt || 0).getTime();
          return dateB - dateA;
        });
        setBlogs(sorted);
        // Display first 8 items
        setDisplayedBlogs(sorted.slice(0, itemsPerPage));
        setCurrentPage(1);
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [itemsPerPage]);

  const handleLoadMore = () => {
    const nextPage = currentPage + 1;
    const startIndex = (nextPage - 1) * itemsPerPage;
    const newBlogs = blogs.slice(0, startIndex + itemsPerPage);
    setDisplayedBlogs(newBlogs);
    setCurrentPage(nextPage);
  };

  const hasMoreBlogs = displayedBlogs.length < blogs.length;

  return (
    <main className="bg-background dark:bg-darkbg text-black dark:text-textDark py-14 sm:py-20 transition-colors duration-300">
      <div className="w-full mx-auto max-w-[1200px] px-6">
        {/* Header */}
        <header className="text-center max-w-3xl mx-auto mb-10 sm:mb-14">
          <p className="text-xs text-muted dark:text-textDark font-medium">
            Blogs
          </p>
          <h1 className="mt-2 text-3xl sm:text-5xl font-extrabold tracking-tight text-black dark:text-textDark">
            Your Guide to Smarter Online Gaming
          </h1>
          <p className="mt-3 text-sm sm:text-base text-muted dark:text-textDark">
            Discover the perfect online casino tailored to your style. Explore
            our curated selection of top-rated platforms, unlock exclusive
            bonuses, and enjoy insights that make your gaming experience
            seamless and rewarding.
          </p>
        </header>

        {/* Grid */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-8 gap-y-12">
          {loading ? (
            Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
          ) : displayedBlogs.length > 0 ? (
            displayedBlogs.map((post) => {
              const slugPart =
                (post.slug && String(post.slug).trim()) || slugify(post.title);
              const href = `/blog/${slugPart}`;
              return (
                <ArticleCard
                  key={post.id}
                  href={href}
                  imageUrl={post.imageUrl}
                  title={post.title}
                  date={formatDate(post.publishedAt)}
                  category={post.category || "Article"}
                  excerpt={post.excerpt}
                />
              );
            })
          ) : (
            <div className="col-span-full text-center text-muted dark:text-textDark">
              No blogs found.
            </div>
          )}
        </section>

        {/* Load More Button */}
        {hasMoreBlogs && (
          <div className="flex justify-center mt-12 sm:mt-16">
            <button
              onClick={handleLoadMore}
              className="px-8 py-3 bg-primary hover:bg-primary-hover text-white font-semibold rounded-lg transition-colors duration-300"
            >
              Load More
            </button>
          </div>
        )}
      </div>
    </main>
  );
};

export default BlogsPage;
